// @ts-nocheck
import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "../DaikinConsistency.style";
import { SortableHeader, applySortOrder, IssueDotHeaderCell, IssueDotCell } from "./shared";
import { SectionedIssuesTable } from "./ConsistencyLineView";

const CACHE_TYPE_LABEL = { outdoor: "Outdoor", control: "Control", service: "Service" };
const CACHE_TYPE_ORDER = ["outdoor", "control", "service"];

const errSx = { color: "error.main", fontWeight: 600 };
const warnSx = { color: "warning.main", fontWeight: 600 };

function cellSx(unit, field) {
  if (unit.issues?.some((i) => i.field === field && i.priority === "high")) { return errSx; }
  if (unit.issues?.some((i) => i.field === field)) { return warnSx; }
  return undefined;
}

function countCacheIssues(data) {
  let high = 0,
    medium = 0;
  for (const typeKey of CACHE_TYPE_ORDER) {
    for (const unit of Object.values(data[typeKey] || {})) {
      for (const issue of unit.issues || []) {
        if (issue.priority === "high") { high++; }
        else { medium++; }
      }
    }
  }
  return { high, medium };
}

// Issues dialog

const CacheIssuesDialog = ({ data, open, onClose }) => {
  const classes = useStyles();
  const { high, medium } = countCacheIssues(data);
  const sections = CACHE_TYPE_ORDER.map((typeKey) => ({
    label: CACHE_TYPE_LABEL[typeKey],
    units: Object.entries(data[typeKey] || {})
      .map(([addr, unit]) => ({
        name: unit.data?.name,
        address: unit.address ?? addr,
        issues: unit.issues || [],
      }))
      .filter((u) => u.issues.length > 0),
  })).filter((s) => s.units.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight={700}>Cache Issues</Typography>
          {high > 0 && <Chip label={`${high} duplicate`} color="error" size="small" className={classes.chipMd} />}
          {medium > 0 && <Chip label={`${medium} missing`} color="warning" size="small" className={classes.chipMd} />}
          <IconButton size="small" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5, height: "min(900px, 75vh)", overflow: "auto" }}>
        <SectionedIssuesTable sections={sections} emptyMessage="No cache issues found." />
      </DialogContent>
    </Dialog>
  );
};

// Main views

const CacheView = ({ result, selectedLine, onLineChange, dkLineIds }) => {
  const classes = useStyles();
  const handlerData = result?.handlerData || {};
  const lineIds = Object.keys(handlerData)
    .filter((id) => !dkLineIds || dkLineIds.has(id))
    .sort((a, b) => Number(a) - Number(b));

  if (lineIds.length === 0) {
    return <Alert severity="info">No cached data found for this device.</Alert>;
  }

  const currentLineId = lineIds[selectedLine];
  const currentLineData = currentLineId ? handlerData[currentLineId] : null;

  return (
    <>
      <Tabs value={selectedLine} onChange={(_, v) => onLineChange(v)} className={classes.tabsBar}>
        {lineIds.map((lineId) => {
          const lineData = handlerData[lineId];
          const totalUnits = CACHE_TYPE_ORDER.reduce((n, t) => n + Object.keys(lineData[t] || {}).length, 0);
          return (
            <Tab
              key={lineId}
              label={
                <Box className={classes.tabLabel}>
                  <span>Line {lineId}</span>
                  <Typography variant="caption" color="text.secondary">
                    ({totalUnits})
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </Tabs>
      {currentLineData && <CacheLineView data={currentLineData} lineId={currentLineId} />}
    </>
  );
};

const CacheLineView = ({ data, lineId }) => {
  const classes = useStyles();
  const [issuesOpen, setIssuesOpen] = useState(false);
  const { high, medium } = countCacheIssues(data);
  const totalIssues = high + medium;

  const summaryParts = CACHE_TYPE_ORDER.map((t) => {
    const n = Object.keys(data[t] || {}).length;
    return n > 0 ? `${n} ${CACHE_TYPE_LABEL[t].toLowerCase()}` : null;
  }).filter(Boolean);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="caption" color="text.secondary">
          {summaryParts.join(" • ")}
        </Typography>
        {totalIssues > 0 && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              {totalIssues} issue(s)
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIssuesOpen(true)}
              title="Show all cache issues"
              sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1 } }}
            >
              <FormatListBulletedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        )}
      </Box>
      {CACHE_TYPE_ORDER.map((type) => {
        const units = data[type];
        if (!units || Object.keys(units).length === 0) { return null; }
        const entries = Object.entries(units);
        const label = CACHE_TYPE_LABEL[type] || type;
        return (
          <Accordion key={type} defaultExpanded className={classes.entitySectionAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.entitySectionSummaryBox}>
                <Typography fontWeight={600} className={classes.entitySectionTitle}>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {entries.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.entitySectionDetails}>
              {type === "outdoor" && <CacheOutdoorTable entries={entries} />}
              {type === "control" && <CacheControlTable entries={entries} />}
              {type === "service" && <CacheServiceTable entries={entries} />}
            </AccordionDetails>
          </Accordion>
        );
      })}
      <CacheIssuesDialog data={data} open={issuesOpen} onClose={() => setIssuesOpen(false)} />
    </Box>
  );
};

// Unit tables

const CacheOutdoorTable = ({ entries }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const mkSort = (col) => setSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));
  const sorted = applySortOrder(entries, sort.col, sort.dir, ([addr, unit], col) => {
    if (col === "address") { return unit.address ?? addr; }
    if (col === "sysNum") { return unit.data?.systemNumber; }
    if (col === "sysId") { return unit.data?.systemId; }
    if (col === "airnet") { return unit.data?.airnet; }
    return null;
  });
  return (
    <TableContainer>
      <Table size="small" className={classes.tableAuto}>
        <TableHead>
          <TableRow>
            <IssueDotHeaderCell />
            <TableCell className={classes.th}>Name</TableCell>
            <SortableHeader col="address" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Address
            </SortableHeader>
            <SortableHeader col="sysNum" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Sys Num
            </SortableHeader>
            <SortableHeader col="sysId" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Sys ID
            </SortableHeader>
            <SortableHeader col="airnet" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Airnet
            </SortableHeader>
            <TableCell className={classes.th}>DB ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([addr, unit]) => (
            <TableRow key={addr}>
              <IssueDotCell issues={unit.issues || []} />
              <TableCell className={classes.td}>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {unit.data?.name || "—"}
                </Typography>
              </TableCell>
              <TableCell className={classes.tdMono}>{unit.address ?? addr}</TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "systemNumber")}>
                {unit.data?.systemNumber ?? "—"}
              </TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "systemId")}>
                {unit.data?.systemId != null ? String(unit.data.systemId) : "—"}
              </TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "airnet")}>
                {unit.data?.airnet ?? "—"}
              </TableCell>
              <TableCell className={classes.tdMono}>
                {unit.dbId ? (
                  unit.dbId.slice(-6)
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    none
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CacheControlTable = ({ entries }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const mkSort = (col) => setSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));
  const sorted = applySortOrder(entries, sort.col, sort.dir, ([addr, unit], col) => {
    if (col === "address") { return unit.address ?? addr; }
    if (col === "indoorId") { return unit.indoorId ?? unit.data?.indoorId; }
    return null;
  });
  return (
    <TableContainer>
      <Table size="small" className={classes.tableAuto}>
        <TableHead>
          <TableRow>
            <IssueDotHeaderCell />
            <TableCell className={classes.th}>Name</TableCell>
            <SortableHeader col="address" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Address
            </SortableHeader>
            <SortableHeader col="indoorId" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Indoor ID
            </SortableHeader>
            <TableCell className={classes.th}>DB ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([addr, unit]) => (
            <TableRow key={addr}>
              <IssueDotCell issues={unit.issues || []} />
              <TableCell className={classes.td}>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {unit.data?.name || "—"}
                </Typography>
              </TableCell>
              <TableCell className={classes.tdMono}>{unit.address ?? addr}</TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "indoorId")}>
                {unit.indoorId != null
                  ? String(unit.indoorId)
                  : unit.data?.indoorId != null
                    ? String(unit.data.indoorId)
                    : "—"}
              </TableCell>
              <TableCell className={classes.tdMono}>
                {unit.dbId ? (
                  unit.dbId.slice(-6)
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    none
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CacheServiceTable = ({ entries }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const mkSort = (col) => setSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));
  const sorted = applySortOrder(entries, sort.col, sort.dir, ([addr, unit], col) => {
    if (col === "address") { return unit.address ?? addr; }
    if (col === "sysNum") { return unit.data?.systemNumber; }
    if (col === "indoorId") { return unit.data?.indoorId; }
    if (col === "mac") { return unit.data?.associatedControlAddress; }
    if (col === "airnet") { return unit.data?.airnet; }
    return null;
  });
  return (
    <TableContainer>
      <Table size="small" className={classes.tableAuto}>
        <TableHead>
          <TableRow>
            <IssueDotHeaderCell />
            <TableCell className={classes.th}>Name</TableCell>
            <SortableHeader col="address" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Address
            </SortableHeader>
            <SortableHeader col="sysNum" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Sys Num
            </SortableHeader>
            <SortableHeader col="indoorId" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Indoor ID
            </SortableHeader>
            <SortableHeader col="mac" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              MAC
            </SortableHeader>
            <SortableHeader col="airnet" current={sort.col} dir={sort.dir} onSort={mkSort} className={classes.th}>
              Airnet
            </SortableHeader>
            <TableCell className={classes.th}>DB ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([addr, unit]) => (
            <TableRow key={addr}>
              <IssueDotCell issues={unit.issues || []} />
              <TableCell className={classes.td}>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {unit.data?.name || "—"}
                </Typography>
              </TableCell>
              <TableCell className={classes.tdMono}>{unit.address ?? addr}</TableCell>
              <TableCell className={classes.tdMono}>{unit.data?.systemNumber ?? "—"}</TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "indoorId")}>
                {unit.data?.indoorId != null ? String(unit.data.indoorId) : "—"}
              </TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "mac")}>
                {unit.data?.associatedControlAddress != null ? String(unit.data.associatedControlAddress) : "—"}
              </TableCell>
              <TableCell className={classes.tdMono} sx={cellSx(unit, "airnet")}>
                {unit.data?.airnet ?? "—"}
              </TableCell>
              <TableCell className={classes.tdMono}>
                {unit.dbId ? (
                  unit.dbId.slice(-6)
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    none
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CacheView;
